import React from 'react';

const ProgressRing = ({ days, color, size = 76, strokeWidth = 9, delay = 0 }) => {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setReady(true), 120 + delay);
    return () => clearTimeout(t);
  }, [delay]);
  const r = (size - strokeWidth) / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = days === null ? 0 : Math.max(0, Math.min(100, (Math.max(days, 0) / 365) * 100));
  const dash = ready ? (pct / 100) * circ : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth={strokeWidth} />
      <circle
        cx={cx} cy={cy} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{ transition: `stroke-dasharray 1s cubic-bezier(0.4,0,0.2,1) ${delay}ms` }}
      />
    </svg>
  );
};

const DocRingCard = ({ type, fullLabel, doc, index, getDocMeta }) => {
  const [hovering, setHovering] = React.useState(false);
  const { color, label, days } = getDocMeta(doc);
  const exp = doc?.exp || doc?.expiryDate;
  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        textAlign: 'center',
        padding: '18px 10px 14px',
        borderRadius: '14px',
        border: `1.5px solid ${hovering ? color : `${color}30`}`,
        background: hovering ? `${color}10` : `${color}06`,
        transition: 'all 0.2s ease',
        transform: hovering ? 'translateY(-2px)' : 'none',
        boxShadow: hovering ? `0 4px 16px ${color}22` : 'none',
        cursor: 'default',
      }}
    >
      <div style={{ position: 'relative', width: 76, height: 76, margin: '0 auto 12px' }}>
        <ProgressRing days={days} color={color} size={76} strokeWidth={9} delay={index * 150} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          {days !== null ? (
            <>
              <span style={{ fontSize: days > 99 ? '14px' : '17px', fontWeight: 800, lineHeight: 1, color }}>
                {days < 0 ? 'EXP' : days > 99 ? '99+' : days}
              </span>
              <span style={{ fontSize: '8px', color: '#94a3b8', marginTop: '2px' }}>{days < 0 ? 'expired' : 'days'}</span>
            </>
          ) : (
            <span style={{ fontSize: '20px', color: '#d1d5db' }}>—</span>
          )}
        </div>
      </div>
      <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-1)', marginBottom: '4px', letterSpacing: '0.02em' }}>{type}</div>
      <div style={{ fontSize: '10px', color: 'var(--text-3)', marginBottom: '8px' }}>{fullLabel}</div>
      <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '9999px', fontSize: '10px', fontWeight: 700, color, background: `${color}18`, marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 500 }}>{exp || '—'}</div>
    </div>
  );
};

const VehicleStatsContent = ({ vehicle }) => {
  const getDays = (exp) => {
    if (!exp) return null;
    const s = String(exp);
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    const date = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
    if (isNaN(date.getTime())) return null;
    return Math.ceil((date - Date.now()) / 86400000);
  };

  const getDocMeta = (doc) => {
    if (!doc) return { color: '#94a3b8', label: 'Not on file', days: null };
    const days = getDays(doc.exp || doc.expiryDate);
    const s = doc.status;
    if (s === 'critical' || (days !== null && days < 14)) return { color: '#ef4444', label: 'Critical', days };
    if (s === 'expiring' || (days !== null && days < 30)) return { color: '#f59e0b', label: 'Expiring', days };
    return { color: '#10b981', label: 'Valid', days };
  };

  const docs = [
    { type: 'RCA', fullLabel: 'Compulsory Insurance', doc: vehicle.docs?.rca },
    { type: 'ITP', fullLabel: 'Technical Inspection', doc: vehicle.docs?.itp },
    { type: 'CASCO', fullLabel: 'Optional Insurance', doc: vehicle.docs?.casco },
  ];

  const statusMeta = vehicle.status === 'Disponibil'
    ? { color: '#10b981', bg: '#ecfdf5' }
    : vehicle.status === 'Alocat'
    ? { color: '#ca8a04', bg: '#fef9c3' }
    : { color: '#ef4444', bg: '#fef2f2' };

  const docsValid = docs.filter(({ doc }) => getDocMeta(doc).label === 'Valid').length;
  const docsExpiring = docs.filter(({ doc }) => getDocMeta(doc).label === 'Expiring').length;
  const docsCritical = docs.filter(({ doc }) => getDocMeta(doc).label === 'Critical').length;
  const docsNone = docs.filter(({ doc }) => !doc).length;

  return (
    <div>
      {/* Vehicle summary strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px', padding: '14px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
            <span className="plate-pill" style={{ fontSize: '12px' }}>{vehicle.plate}</span>
            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-1)' }}>{vehicle.brand} {vehicle.model}</span>
            <span style={{ padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: statusMeta.color, background: statusMeta.bg }}>
              {vehicle.status}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '12px', color: 'var(--text-3)' }}>
            {vehicle.year && <span>{vehicle.year}</span>}
            {vehicle.fuel && <span>{vehicle.fuel}</span>}
            {vehicle.mileage && <span>{Number(vehicle.mileage).toLocaleString()} km</span>}
            {vehicle.company && <span>{vehicle.company}</span>}
          </div>
        </div>
        {/* Mini doc health summary */}
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {docsValid > 0 && <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#10b981', background: '#ecfdf5' }}>{docsValid} Valid</span>}
          {docsExpiring > 0 && <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#f59e0b', background: '#fffbeb' }}>{docsExpiring} Expiring</span>}
          {docsCritical > 0 && <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#ef4444', background: '#fef2f2' }}>{docsCritical} Critical</span>}
          {docsNone === 3 && <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#94a3b8', background: '#f1f5f9' }}>No docs</span>}
        </div>
      </div>

      {/* Document health rings */}
      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-1)', marginBottom: '14px', letterSpacing: '0.01em' }}>Document Health</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {docs.map(({ type, fullLabel, doc }, i) => (
          <DocRingCard key={type} type={type} fullLabel={fullLabel} doc={doc} index={i} getDocMeta={getDocMeta} />
        ))}
      </div>
    </div>
  );
};

export default VehicleStatsContent;
